import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { HOME_MEDIA, ytThumb } from '../mock';
import Reveal from './Reveal';
import YoutubeDialog from './YoutubeDialog';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const PHOTOS = HOME_MEDIA.filter((i) => i.kind === 'photo');

const roundBtn =
  'flex items-center justify-center rounded-full border text-neutral-300 transition-colors duration-300 hover:border-gold hover:text-gold';

const pad = (n) => String(n).padStart(2, '0');

const MediaHeading = ({ onScroll }) => (
  <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
    <div>
      <Reveal>
        <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">Media</p>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="font-serif text-4xl font-light leading-tight tracking-[0.04em] text-white md:text-6xl">
          PHOTOS, PODCASTS,
          <br />
          VIDEOS &amp; HIGHLIGHTS
        </h2>
      </Reveal>
    </div>
    <Reveal delay={200}>
      <div className="flex items-center gap-6 pb-2">
        <Link
          to="/gallery"
          data-testid="explore-all-media"
          className="group inline-flex items-center gap-2 text-[10px] tracking-[0.3em] text-neutral-300 transition-colors duration-300 hover:text-gold"
        >
          EXPLORE ALL MEDIA
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <div className="hidden items-center gap-2 sm:flex">
          <button onClick={() => onScroll(-1)} aria-label="Scroll gallery left" className={`${roundBtn} h-10 w-10 border-white/25`}>
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => onScroll(1)} aria-label="Scroll gallery right" className={`${roundBtn} h-10 w-10 border-white/25`}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </Reveal>
  </div>
);

const MediaCard = ({ item, onOpen }) => (
  <button
    data-testid={`home-media-${item.id}`}
    onClick={() => onOpen(item)}
    className="group relative min-w-[260px] snap-start overflow-hidden text-left sm:min-w-[320px] md:min-w-[360px]"
    aria-label={`Open ${item.title}`}
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={item.image || ytThumb(item.youtubeId)}
        alt={item.title}
        loading="lazy"
        className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
          item.pos === 'top' ? 'object-top' : ''
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
      {item.kind !== 'photo' && (
        <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink shadow-lg transition-transform duration-300 group-hover:scale-110">
          <Play size={14} fill="currentColor" className="ml-0.5" />
        </span>
      )}
      <div className="absolute bottom-4 left-5 right-5">
        <p className="text-[9px] tracking-[0.35em] text-gold">{item.tag}</p>
        <p className="mt-1 font-serif text-lg leading-snug tracking-[0.06em] text-white">{item.title}</p>
      </div>
    </div>
  </button>
);

const PhotoLightbox = ({ index, onClose, onStep }) => {
  const active = index === null ? null : PHOTOS[index];
  const navBtn = `${roundBtn} absolute top-1/2 h-11 w-11 -translate-y-1/2 border-white/30 bg-ink/50 backdrop-blur-sm`;
  return (
    <Dialog open={!!active} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-ink p-0 text-neutral-200 sm:max-w-3xl">
        <DialogTitle className="sr-only">{active?.title || 'Gallery'}</DialogTitle>
        <DialogDescription className="sr-only">Media gallery lightbox</DialogDescription>
        {active && (
          <div>
            <div className="relative aspect-[4/3] w-full overflow-hidden sm:aspect-[16/10]">
              <img
                src={active.image}
                alt={active.title}
                className={`h-full w-full object-cover ${active.pos === 'top' ? 'object-top' : ''}`}
              />
              <button onClick={() => onStep(-1)} aria-label="Previous image" className={`${navBtn} left-4`}>
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => onStep(1)} aria-label="Next image" className={`${navBtn} right-4`}>
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex items-center justify-between px-6 py-5">
              <div>
                <p className="text-[9px] tracking-[0.35em] text-gold">{active.tag}</p>
                <p className="mt-1 font-serif text-xl tracking-[0.06em] text-white">{active.title}</p>
              </div>
              <p className="text-[10px] tracking-[0.25em] text-neutral-500">
                {pad(index + 1)} / {pad(PHOTOS.length)}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Media = () => {
  const trackRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [video, setVideo] = useState(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const openItem = (item) => {
    if (item.kind === 'photo') setLightbox(PHOTOS.indexOf(item));
    else setVideo(item);
  };

  const step = (dir) => setLightbox((cur) => (cur + dir + PHOTOS.length) % PHOTOS.length);

  return (
    <section id="media" className="bg-ink py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <MediaHeading onScroll={scrollBy} />
      </div>
      <Reveal delay={150}>
        <div ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 md:px-10">
          {HOME_MEDIA.map((item) => (
            <MediaCard key={item.id} item={item} onOpen={openItem} />
          ))}
        </div>
      </Reveal>
      <PhotoLightbox index={lightbox} onClose={() => setLightbox(null)} onStep={step} />
      <YoutubeDialog video={video} onClose={() => setVideo(null)} />
    </section>
  );
};

export default Media;
